import { VesselGraphic } from '@deckee/utilities';
import { HULL_ART_WIDTH } from '../constants';
import type { BoatPalette } from './boatPalette';
import { drawJetSki } from './drawJetSki';
import { drawKayak } from './drawKayak';
import { drawMotorLarge } from './drawMotorLarge';
import { drawMotorMedium } from './drawMotorMedium';
import { drawMotorSmall } from './drawMotorSmall';
import { drawSailVessel } from './drawSailVessel';
import type { VesselDrawFn, VesselDrawState } from './types';

export type { VesselDrawState } from './types';
export { BoatPalette } from './boatPalette';

const DRAW_BY_GRAPHIC: Record<VesselGraphic, VesselDrawFn> = {
  [VesselGraphic.Sail]: drawSailVessel,
  [VesselGraphic.JetSki]: drawJetSki,
  [VesselGraphic.Kayak]: drawKayak,
  [VesselGraphic.MotorSmall]: drawMotorSmall,
  [VesselGraphic.MotorMedium]: drawMotorMedium,
  [VesselGraphic.MotorLarge]: drawMotorLarge,
};

export interface DrawBoatOptions {
  palette: BoatPalette;
  graphic: VesselGraphic;
  /** Hull centre, screen px. */
  centreX: number;
  /** Still-water line, screen px. */
  baseY: number;
  /** On-screen hull width, px. The artwork scales to this. */
  hullPx: number;
  /** Current heave, px (positive up). */
  heavePx: number;
  /** Current pitch, radians. */
  pitchRad: number;
  /** Capsize progress, 0 upright to 1 fully inverted. */
  capsize: number;
  /** Draw-only inland chop offset. Never feeds heave, slam or capsize physics. */
  judderYPx: number;
  /** Draw-only inland chop rotation, radians. */
  judderRotationRad: number;
  /** How dark it is, 0 (day) to 1 (night). */
  night: number;
  windMps: number;
  reducedMotion: boolean;
  timeS: number;
}

/**
 * Place, orient and draw the vessel.
 *
 * Pitch stays fully active while capsized and a `capsize × π` roll is added on
 * top, so an inverted hull rides up and down the swell exactly as the upright
 * one does — it is the same boat, just the other way up.
 */
export const drawBoat = ({
  palette,
  graphic,
  centreX,
  baseY,
  hullPx,
  heavePx,
  pitchRad,
  capsize,
  judderYPx,
  judderRotationRad,
  night,
  windMps,
  reducedMotion,
  timeS,
}: DrawBoatOptions): void => {
  const { ctx } = palette;
  const scale = hullPx / HULL_ART_WIDTH;
  const y = baseY - heavePx + capsize * 3 + judderYPx;

  const state: VesselDrawState = {
    palette,
    night,
    pitchRad,
    windMps,
    reducedMotion,
    timeS,
  };

  ctx.save();
  ctx.translate(centreX, y);
  ctx.rotate(-pitchRad - capsize * Math.PI + judderRotationRad);
  ctx.scale(scale, scale);

  (DRAW_BY_GRAPHIC[graphic] ?? drawMotorLarge)(state);

  ctx.restore();
};
